import React from "react";
import { useRouter } from "next/router";
import { isStringWithContent } from "@shared/utils/text/utils";
import Accommodations from "@app-contexts/public/features/event-detail/components/Accommodations/Accommodations.web";
import useDetailPageScrollMargin from "@app-contexts/public/features/event-detail/hooks/useDetailPageScrollMargin";
import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import HelpAndInfoModalSelecton from "@shared/modules/seating/components/helpAndInfoModal/HelpAndInfoModalSelection";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import ErrorBoundary from "@shared/components/application/other/ErrorBoundary/ErrorBoundary.web";
import useNativeAppCheck from "@shared/hooks/platform/useNativeAppCheck";
import DetailCardContainer from "@ui-kit/data-display/DetailCardContainer/DetailCardContainer.web";
import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import Button from "@ui-kit/form/Button/Button.web";
import Icon from "@ui-kit/icons/Icon.web";
import ParsedHtml from "@ui-kit/typography/ParsedHtml/ParsedHtml.web";
import { useTranslation } from "@shared/utils/internationalization";
import { EventRestrictions } from "../events/EventRestrictions.web";
import { UpcomingEventsSlider } from "./UpcomingEventsSlider.web";

export interface DetailPageInfoProps {
  infoHeader: string;
  infoText: string;
  infoRestrictions?: string[];
  refundPolicyText: string;
  isLoading: boolean;
  isAttractionPage: boolean;
  openContactDetailsModal?: () => void;
  venueInfo: {
    venueId: number | undefined;
    venueSlug: string | undefined;
    venueDescription: string | undefined;
    venueName: string | undefined;
    venuePrivate: boolean | undefined;
  };
  /** Event ID for excluding from upcoming events list */
  eventId?: number;
  /** URL for accommodations section (optional) */
  accommodationUrl?: string;
}

export const DetailPageInfo: React.FC<DetailPageInfoProps> = ({
  infoHeader,
  infoText,
  infoRestrictions = [],
  openContactDetailsModal = () => {
    /* empty */
  },
  refundPolicyText,
  isLoading,
  isAttractionPage,
  venueInfo,
  eventId,
  accommodationUrl,
}) => {
  const { t } = useTranslation("purchases");
  const router = useRouter();
  const { isReactNativeApp } = useNativeAppCheck();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { usePublicUpcomingEvents } = usePublicVenueService();

  const upcomingEventsPageSize = 9;

  // Get a venue's upcoming events and display them on the purchase page
  // Do not display this component on attraction pages
  const { upcomingEvents } = usePublicUpcomingEvents(
    venueInfo.venueSlug || "",
    {
      page_size: upcomingEventsPageSize,
      exclude_events: eventId?.toString(),
    },
    { enabled: !isAttractionPage }
  );

  const skeletonText = "skeletonText";

  const contactButtonInfo = [
    {
      id: 0,
      icon: (
        <Icon.User
          boxSize="element.2xl"
          mr={{ base: "sm", lg: "none" }}
          aria-label={t("profile icon")}
        />
      ),
      name: t("Organizer Profile"),
      handleClick: () => {
        router.push(`/o/${venueInfo.venueSlug}/`);
      },
      showButton: !isReactNativeApp && !isLoading,
      dataTestId: "organizer-profile-button",
    },
    {
      id: 1,
      icon: (
        <Icon.Info
          boxSize="element.2xl"
          mr={{ base: "sm", lg: "none" }}
          aria-label={t("help icon")}
        />
      ),
      name: t("Help & Info"),
      handleClick: () => {
        onOpen();
      },
      showButton: !isLoading,
      dataTestId: "help-&-info-button",
    },
    {
      id: 2,
      icon: (
        <Icon.Call
          boxSize="element.2xl"
          mr={{ base: "sm", lg: "none" }}
          aria-label={t("contact icon")}
        />
      ),
      name: t("Contact Us"),
      handleClick: () => {
        if (openContactDetailsModal) {
          openContactDetailsModal();
        }
      },
      showButton: !venueInfo.venuePrivate && !isLoading,
      dataTestId: "contact-us-button",
    },
  ];

  const visibleButtons = contactButtonInfo.filter(
    (filterButton) => filterButton.showButton
  );

  const scrollMargin = useDetailPageScrollMargin();

  return (
    <>
      {isLoading && (
        <Flex direction="column" rowGap="xl" w="full">
          <DetailCardContainer>
            <Skeleton mb="xs">
              <Text fontSize={{ base: "md", sm: "lg" }}>{skeletonText}</Text>
            </Skeleton>

            <Skeleton height="element.4xl" />
          </DetailCardContainer>

          <DetailCardContainer>
            <Skeleton mb="xs">
              <Text fontSize={{ base: "md", sm: "lg" }}>{skeletonText}</Text>
            </Skeleton>

            <Skeleton mb="md">
              <Text fontSize="xs">{skeletonText}</Text>
            </Skeleton>

            <Flex
              direction={{ base: "column", lg: "row" }}
              justifyContent="space-between"
              gap="xl"
            >
              {contactButtonInfo.map((button) => (
                <Skeleton
                  p="lg"
                  width="full"
                  // TODO: Add element token for 90px (between element.6xl=80px and element.7xl=100px)
                  // eslint-disable-next-line showpass-custom/no-hardcoded-style-values
                  height="90px"
                  key={button.id}
                  borderRadius="md"
                >
                  {button.name}
                </Skeleton>
              ))}
            </Flex>
          </DetailCardContainer>

          <DetailCardContainer>
            <Skeleton mb="xs">
              <Text fontSize={{ base: "md", sm: "lg" }}>{skeletonText}</Text>
            </Skeleton>
            <Skeleton height="element.4xl" />
          </DetailCardContainer>

          <DetailCardContainer>
            <Skeleton mb="xs">
              <Text fontSize={{ base: "md", sm: "lg" }}>{skeletonText}</Text>
            </Skeleton>
            {/* TODO: Add element token for 300px (no exact match in design system) */}
            {/* eslint-disable-next-line showpass-custom/no-hardcoded-style-values */}
            <Skeleton height="300px" />
          </DetailCardContainer>
        </Flex>
      )}

      {!isLoading && (
        <Flex direction="column" w="full" rowGap="xl">
          <HelpAndInfoModalSelecton
            venueRefundPolicy={refundPolicyText}
            isOpen={isOpen}
            onClose={onClose}
          />
          {infoText && isStringWithContent(infoText) && (
            <DetailCardContainer data-testid="description-container">
              <Text
                mb="xs"
                fontSize={{ base: "md", sm: "lg" }}
                fontWeight="bold"
                color="fg"
              >
                {infoHeader}
              </Text>
              {infoText && <ParsedHtml htmlString={infoText} />}
            </DetailCardContainer>
          )}

          {infoRestrictions && infoRestrictions.length > 0 && (
            <DetailCardContainer data-testid="important-info-container">
              <Flex
                id="important-info"
                scrollMarginTop={scrollMargin}
                direction="column"
              >
                <Text
                  mb="xs"
                  fontSize={{ base: "md", sm: "lg" }}
                  fontWeight="bold"
                  color="fg"
                >
                  {t("Important Info")}
                </Text>
                <EventRestrictions restrictionArray={infoRestrictions} />
              </Flex>
            </DetailCardContainer>
          )}

          <ErrorBoundary fallback={null}>
            {accommodationUrl && <Accommodations url={accommodationUrl} />}
          </ErrorBoundary>

          <ErrorBoundary fallback={null}>
            <DetailCardContainer data-testid="contact-information-container">
              <Text
                mb="xs"
                fontSize={{ base: "md", sm: "lg" }}
                fontWeight="bold"
                color="fg"
              >
                {t("Contact Information")}
              </Text>

              {venueInfo.venueDescription ? (
                <Text mb="md" fontSize="xs" color="fg.muted">
                  {venueInfo.venueDescription}
                </Text>
              ) : (
                <></>
              )}

              <Flex
                direction={{ base: "column", lg: "row" }}
                justifyContent="space-between"
                gap="xl"
              >
                {visibleButtons.map((button) => (
                  <Button
                    key={button.id}
                    cursor="pointer"
                    display="flex"
                    flexDirection="column"
                    alignItems={{ base: "flex-start", lg: "center" }}
                    flexGrow={1}
                    h="full"
                    p="md"
                    fontSize="sm"
                    bg="bg.muted"
                    borderRadius="md"
                    fontWeight="default"
                    _hover={{
                      _light: { bg: "gray.400" },
                      _dark: { bg: "whiteAlpha.400" },
                    }}
                    onClick={() => button.handleClick()}
                    data-testid={button.dataTestId}
                  >
                    <Flex
                      direction={{ base: "row", lg: "column" }}
                      alignItems="center"
                      color="fg"
                    >
                      <span>{button.icon}</span>
                      <Text wordBreak="break-word" pt="2xs" color="primary.600">
                        {button.name}
                      </Text>
                    </Flex>
                  </Button>
                ))}
              </Flex>
            </DetailCardContainer>
          </ErrorBoundary>

          <DetailCardContainer data-testid="refund-policy-container">
            <Text
              mb="xs"
              fontSize={{ base: "md", sm: "lg" }}
              fontWeight="bold"
              color="fg"
            >
              {t("Refund Policy")}
            </Text>
            <Text fontSize="sm" color="fg">
              {refundPolicyText
                ? t(`${refundPolicyText}`)
                : t("This venue hasn't specified a refund policy.")}
            </Text>
          </DetailCardContainer>

          <ErrorBoundary fallback={null}>
            {upcomingEvents &&
              upcomingEvents?.length > 0 &&
              !isReactNativeApp && (
                <DetailCardContainer data-testid="more-events-purchase-container">
                  <Text
                    mb="md"
                    fontSize={{ base: "md", sm: "lg" }}
                    fontWeight="bold"
                    color="fg"
                  >
                    {t("More Events From This Organizer")}
                  </Text>

                  <UpcomingEventsSlider upcomingEvents={upcomingEvents} />
                </DetailCardContainer>
              )}
          </ErrorBoundary>
        </Flex>
      )}
    </>
  );
};

export default DetailPageInfo;
